import { v4 as genUuid } from "uuid";
import { ItemDisplayProperties, ItemType, Asset } from "@thenounproject/lingo-core";
import { snakeCase, mapKeys } from "lodash";
import {
  generateInsertPositionForUploads,
  getItemData,
  UploadAction,
  uploadActionForFile,
  type InsertPosition,
} from "@actions/uploads";
import { createAsyncAction } from "../actionCreators";
import { fetchContentTypes } from "../useFetchContentTypes";
import { enqueueUpload } from "../uploads/enqueueUpload";

type Args = {
  files: File[];
  itemType: ItemType;
  insertPosition: InsertPosition;
  displayProperties?: ItemDisplayProperties;
  previewBackground?: Asset["meta"]["backgroundColor"];
  batchId?: string;
};

const [useCreateFileAssets, createFileAssets] = createAsyncAction(
  "assets/createFileAsset",
  async (
    {
      files,
      itemType,
      insertPosition: startingInsertPosition,
      displayProperties,
      previewBackground,
      batchId: providedBatchId,
    }: Args,
    thunkApi
  ) => {
    const contentTypes = await fetchContentTypes.lazy(thunkApi, { itemType });
    const batchId = providedBatchId ?? genUuid();
    const insertPosition = generateInsertPositionForUploads(files.length, startingInsertPosition);

    /**
     * Queue up the uploads with meta
     */
    files
      .map(file => {
        const { upload, asset } = uploadActionForFile(file, contentTypes, insertPosition, batchId);
        asset.space_id = insertPosition.spaceId;
        upload.isAsset = itemType === ItemType.asset;
        const { item } = getItemData(insertPosition);
        if (item) {
          item.type = itemType;
          if (displayProperties) {
            item.display_properties = mapKeys(displayProperties, (_, key) => snakeCase(key));
          }
        }
        asset.item = item;
        if (previewBackground) {
          asset.meta = { ...asset.meta, background_color: previewBackground };
        }
        return {
          upload,
          requestData: {
            endpoint: "assets",
            method: "POST",
            file: file,
            data: asset,
          },
        } as UploadAction;
      })
      .forEach(action => {
        thunkApi.dispatch(enqueueUpload(action));
      });
  }
);
export default useCreateFileAssets;
export { createFileAssets };
